import { supabase } from './supabase';
import type {
  Entitlement,
  ProfessionalWorkspace,
  RoadmapPath
} from '../types/progress';
import type { AiAction, AiMessage, AiState, AiThread } from '../types/ai';
import type { AdminState } from '../types/admin';
import type { AssessmentDraft } from '../auth/assessmentDraft';

async function accessToken() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in required');
  return token;
}

async function authenticatedPost<T>(endpoint: string, body: Record<string, unknown> = {}) {
  const token = await accessToken();
  const response = await fetch(`/.netlify/functions/${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'Request failed') as Error & {
      status?: number;
      payload?: Record<string, unknown>;
    };
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload as T;
}

export function getEntitlement(includeConversion = false) {
  return authenticatedPost<Entitlement>('get-entitlement', {
    includeConversion
  });
}

export function saveAssessmentResult(draft: AssessmentDraft) {
  return authenticatedPost<{
    saved: true;
    changed: boolean;
    assessmentResultId: string;
    title: string;
    domain: string;
    domainName: string | null;
    engineVersion: string;
  }>('save-assessment-result', { draft });
}

export type CurriculumWeek = {
  id: string;
  week_index: number;
  title: string;
  objective: string | null;
  why_it_matters: string | null;
  deliverable: string | null;
  completion_criteria: string | null;
  estimated_hours: number | null;
  status: 'locked' | 'active' | 'in_progress' | 'completed';
  proofNote: string | null;
  completedAt: string | null;
};

export type PathCurriculum = {
  status: 'generated' | 'reviewed' | 'published' | 'pending_review' | 'retry_available';
  careerPathKey: string;
  careerPathLabel: string;
  levelIndex: number;
  weeks: CurriculumWeek[];
};

export function getPathCurriculum(lang: 'en' | 'it', levelIndex = 0) {
  return authenticatedPost<PathCurriculum>('get-path-curriculum', { lang, levelIndex });
}

export function getProfessionalWorkspace(
  lang: 'en' | 'it',
  path?: RoadmapPath,
  // Chosen career destination. Omitted means "use whatever is persisted", so a
  // plain reload still returns the roadmap for the path the user picked.
  destination?: 'ideal' | 'accessible' | 'independent' | null
) {
  return authenticatedPost<ProfessionalWorkspace>('get-professional-workspace', {
    lang,
    ...(path ? { path } : {}),
    ...(destination ? { destination } : {})
  });
}

export function updateProfessionalProgress<T = Record<string, unknown>>(
  body: Record<string, unknown>
) {
  return authenticatedPost<T>('update-professional-progress', body);
}

export function getAiState(threadId?: string | null) {
  return authenticatedPost<AiState>('ai-state', {
    action: 'list',
    threadId: threadId || null
  });
}

export function updateAiState<T = Record<string, unknown>>(
  body: Record<string, unknown>
) {
  return authenticatedPost<T>('ai-state', body);
}

export function sendAiMessage(
  message: string,
  lang: 'en' | 'it',
  threadId?: string | null,
  contextEntity?: { type: 'opportunity'; id: string } | null
) {
  return authenticatedPost<{
    thread: AiThread;
    messages: AiMessage[];
    actions: AiAction[];
    usage: { limit: number; used: number; remaining: number };
  }>('ai-chat', {
    message,
    lang,
    threadId: threadId || null,
    contextEntity: contextEntity || null
  });
}

export function getAdminState() {
  return authenticatedPost<AdminState>('admin-state');
}

export function runAdminAction<T = Record<string, unknown>>(
  body: Record<string, unknown>
) {
  return authenticatedPost<T>('admin-action', body);
}

export function createCheckoutSession(
  locale: 'en' | 'it' = 'en',
  billingPeriod: 'monthly' | 'annual' = 'monthly',
  _migrateLegacy = false,
  offer: 'professional_subscription' | 'career_sprint_30d' = 'professional_subscription'
) {
  return authenticatedPost<{
    url: string;
    provider: 'gumroad' | 'gumroad_order';
    offer?: 'professional_subscription' | 'career_sprint_30d';
    alreadySubscribed?: boolean;
    activationRequired?: boolean;
    automaticActivation?: boolean;
  }>('create-checkout-session', { locale, billingPeriod, offer });
}

export function redeemGumroadLicense(
  licenseKey: string,
  offer: 'professional_subscription' | 'career_sprint_30d' = 'professional_subscription'
) {
  return authenticatedPost<{
    professional: true;
    status: string;
    offer?: 'professional_subscription' | 'career_sprint_30d';
  }>('redeem-gumroad-license', { licenseKey, offer });
}

export function createPortalSession() {
  return authenticatedPost<{
    url: string;
    provider: 'lemon_squeezy' | 'gumroad' | 'gumroad_order';
  }>('create-portal-session');
}

export function exportAccountData() {
  return authenticatedPost<Record<string, unknown>>('export-account');
}

export function deleteAccount() {
  return authenticatedPost<{ deleted: true }>('delete-account', {
    confirm: 'DELETE'
  });
}

export type AssessmentHistoryStatus = {
  previousResults: number;
  archivedResults: number;
  previousSessions: number;
  hasCurrentResult: boolean;
  results: AssessmentResultPreview[];
};

export type AssessmentResultPreview = {
  id: string;
  current: boolean;
  version: number;
  title: string;
  domain: string | null;
  domainName: string | null;
  archetype: string | null;
  archetypeName: string | null;
  category: string | null;
  createdAt: string | null;
};

export function getAssessmentHistoryStatus() {
  return authenticatedPost<AssessmentHistoryStatus>(
    'manage-assessment-history',
    { action: 'status' }
  );
}

export function deleteAssessmentResult(resultId: string) {
  return authenticatedPost<{
    deleted: true;
    deletedResultId: string;
    currentChanged: boolean;
    currentResultPreserved?: boolean;
    replacement?: AssessmentResultPreview;
  }>('manage-assessment-history', {
    action: 'delete',
    resultId,
    confirm: 'DELETE_RESULT'
  });
}

export function activateAssessmentResult(resultId: string) {
  return authenticatedPost<{
    activated: true;
    result: AssessmentResultPreview;
  }>('manage-assessment-history', {
    action: 'activate',
    resultId,
    confirm: 'ACTIVATE_RESULT'
  });
}
